import { Badge, Button, Popover, ScrollArea, Skeleton, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconChevronDown, IconClock, IconGitCompare, IconUser } from '@tabler/icons-react';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { useSetAsTrunk } from '~/hooks/test/useSetAsTrunk';
import { api, type RouterOutputs } from '~/trpc-extra/api';
import VersionComparison from './VersionComparison';
import CreatedBy from '../CreatedBy';

export function VersionSwitcher({
  testId,
  selectedTestContentVersion,
  setSelectedTestContentVersion,
}: {
  testId: string;
  selectedTestContentVersion: string;
  setSelectedTestContentVersion: (version: string) => void;
}) {
  const { data: test, isLoading: isTestLoading } = api.test.getTest.useQuery({ id: testId });
  const { data: versionedContents, isLoading: isVersionedContentsLoading } =
    api.test.listPublishedVersionedContents.useQuery({ testId }, { enabled: !!test });

  const userIds = useMemo(
    () => versionedContents?.map((v) => v.publishedByUserId).filter((userId) => userId !== null) ?? [],
    [versionedContents],
  );

  const { data: users } = api.team.getUsersInfo.useQuery({ userIds }, { enabled: userIds.length > 0 });

  return (
    <VersionSwitcherContent
      selectedTestContentVersion={selectedTestContentVersion || test?.trunkContentId || ''}
      setSelectedTestContentVersion={setSelectedTestContentVersion}
      test={test}
      versionedContents={versionedContents || []}
      users={users || {}}
      isLoading={isTestLoading || isVersionedContentsLoading}
    />
  );
}

function VersionSwitcherContent({
  test,
  versionedContents,
  users,
  selectedTestContentVersion,
  setSelectedTestContentVersion,
  isLoading,
}: {
  test?: RouterOutputs['test']['getTest'];
  versionedContents: RouterOutputs['test']['listPublishedVersionedContents'];
  users: RouterOutputs['team']['getUsersInfo'];
  selectedTestContentVersion: string;
  setSelectedTestContentVersion: (version: string) => void;
  isLoading: boolean;
}) {
  const [popoverOpen, setPopoverOpen] = useState(false);

  const setAsTrunk = useSetAsTrunk();

  const selectedTestContentId = useMemo(
    () => versionedContents?.find((v) => v.id === selectedTestContentVersion)?.id || test?.trunkContentId,
    [versionedContents, selectedTestContentVersion, test?.trunkContentId],
  );

  const openVersionComparison = () => {
    setPopoverOpen(false);
    modals.open({
      title: 'Version Comparison',
      size: '90%',
      zIndex: 320,
      styles: {
        body: { height: '80vh', overflow: 'auto' },
      },
      children: <VersionComparison testId={test?.id || ''} />,
    });
  };

  return (
    <Popover
      width={600}
      position="bottom-end"
      opened={popoverOpen}
      onChange={setPopoverOpen}
      shadow="md"
      radius="md"
    >
      <Popover.Target>
        <Button
          variant="default"
          rightSection={<IconChevronDown size={16} />}
          leftSection={<IconClock size={16} />}
          onClick={() => setPopoverOpen(!popoverOpen)}
        >
          {!test || selectedTestContentId === test?.trunkContentId || selectedTestContentId === undefined ?
            'Versions'
          : `Version #${selectedTestContentId?.slice(0, 5)}`}
        </Button>
      </Popover.Target>

      <Popover.Dropdown className="w-full p-0">
        <ScrollArea.Autosize
          mah={500}
          scrollbarSize={6}
        >
          <div className="p-2">
            <div className="mb-2 mt-2 flex items-center justify-end">
              <Button
                variant="light"
                leftSection={<IconGitCompare size={16} />}
                onClick={openVersionComparison}
              >
                Compare versions
              </Button>
            </div>
            {isLoading ?
              <div className="grid h-full grid-cols-4 items-center justify-center gap-4">
                <Skeleton
                  height={48}
                  className="col-span-3"
                />
                <Skeleton
                  height={48}
                  className="col-span-1"
                />
              </div>
            : (versionedContents || []).map((testContent) => {
                const testContentId = testContent.id;
                return (
                  <div
                    key={testContentId}
                    onClick={() => {
                      setSelectedTestContentVersion(testContentId === test?.trunkContentId ? '' : testContentId);
                      setPopoverOpen(false);
                    }}
                    className={clsx(
                      'cursor-pointer rounded-md p-4 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800',
                      {
                        'border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20':
                          testContentId === selectedTestContentId,
                      },
                    )}
                  >
                    <div className="flex w-full flex-col">
                      <div className="flex items-baseline justify-between gap-3">
                        <Text
                          size="md"
                          className="font-semibold"
                        >
                          {`Version #${testContentId?.slice(0, 5)}`}
                        </Text>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-6">
                          <Text
                            size="sm"
                            c="dimmed"
                          >
                            {testContent.updatedAt.toLocaleString()}
                          </Text>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <IconUser
                              className="flex-shrink-0"
                              size={14}
                            />
                            Published by{' '}
                            {testContent.publishedByUserId ?
                              <CreatedBy email={users[testContent.publishedByUserId]?.email} />
                            : 'user'}
                          </div>
                        </div>
                        {testContentId !== test?.trunkContentId ?
                          <div className="flex items-center gap-2">
                            <Button
                              variant="default"
                              onClick={() => {
                                setPopoverOpen(false);
                                setSelectedTestContentVersion(testContentId);
                              }}
                            >
                              View
                            </Button>
                            <Button
                              variant="default"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setPopoverOpen(false);
                                setAsTrunk({ testContentId });
                              }}
                            >
                              Revert
                            </Button>
                          </div>
                        : <Badge>Current Version</Badge>}
                      </div>
                    </div>
                  </div>
                );
              })
            }
          </div>
        </ScrollArea.Autosize>
      </Popover.Dropdown>
    </Popover>
  );
}
